import html2canvas from "html2canvas";

/** Capture the current canvas-card content as a PNG dataURL */
export async function captureCanvasCard() {
  const card = document.querySelector('[data-testid="canvas-card"]');
  if (!card) return null;

  // If a WebGL canvas exists (3D view), capture it directly — html2canvas often
  // misses WebGL contents even with preserveDrawingBuffer.
  const webglCanvas = card.querySelector("canvas");
  if (webglCanvas) {
    try {
      return webglCanvas.toDataURL("image/png");
    } catch (e) {
      console.warn("WebGL canvas capture failed, falling back to html2canvas", e);
    }
  }

  // For SVG views (Site Plan + 2D Plan): capture the full SVG including
  // anything past the visible scroll area so the PROJECT TOTALS / PARKING
  // cards at the bottom always make it into the snapshot.
  const svg = card.querySelector("svg");
  let captureTarget = card;
  let captureW, captureH;
  if (svg) {
    const w = svg.getAttribute("width");
    const h = svg.getAttribute("height");
    if (w && h) {
      captureW = parseFloat(w);
      captureH = parseFloat(h);
      captureTarget = svg.parentElement || card;
    }
  }

  const opts = {
    backgroundColor: "#070b10",
    scale: 1.2,
    logging: false,
    useCORS: true,
  };
  if (captureW && captureH) {
    opts.width = captureW;
    opts.height = captureH;
    opts.windowWidth = captureW;
    opts.windowHeight = captureH;
  }
  const result = await html2canvas(captureTarget, opts);
  return result.toDataURL("image/png");
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Cycle through all views, capturing PNG snapshots for the PDF.
 * Returns an array of { title, dataURL } in presentation order.
 */
export async function captureAllSnapshots({
  setView, setActiveFloor, setPreset,
  onProgress,
}) {
  const out = [];
  const steps = [
    { title: "3D · Default 3/4 View",      kind: "3d",  preset: "default",   floor: 1 },
    { title: "3D · Isometric View",        kind: "3d",  preset: "isometric", floor: 1 },
    { title: "3D · Top-down View",         kind: "3d",  preset: "topdown",   floor: 1 },
    { title: "3D · Front Elevation",       kind: "3d",  preset: "front",     floor: 1 },
    { title: "2D Plan — Ground Floor", kind: "2d", floor: 1 },
    { title: "2D Plan — Level 2", kind: "2d", floor: 2 },
    { title: "2D Plan — Level 3", kind: "2d", floor: 3 },
    { title: "Site Plan — 6 Bldgs · 192 Units · 93 Parking (Project Totals & Parking Cards)", kind: "site" },
  ];

  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    onProgress?.(i + 1, steps.length, s.title);
    if (s.kind === "3d") {
      setView("3d");
      setActiveFloor(s.floor);
      setPreset(s.preset);
    } else if (s.kind === "2d") {
      setView("2d");
      setActiveFloor(s.floor);
    } else if (s.kind === "site") {
      setView("site");
    }
    // Wait for React state + render + WebGL frame
    await sleep(s.kind === "3d" ? 1200 : 700);
    const dataURL = await captureCanvasCard();
    if (dataURL) out.push({ title: s.title, dataURL });
  }
  return out;
}
